import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { LoginRequest, LoginResponse, UserInfo as AuthUserInfo } from '../types/auth';
import { ContractInformation, SiteTypeRecord, ContractModel, ContractQueryModel, ContractResponse, SiteTypeModifyRecord } from '../types/contract';
import { MixIDQueryModel, MixIDResponse, TaskQueryModel, TaskResponse } from '../types/mixID';
import { SiteSlumpQueryModel, SiteSlumpResponse, Form17ExportRequest } from '../types/siteSlump';
import {
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  UserInfo,
  UserListResponse,
  UserQueryModel,
  ApiResponse
} from '../types/user';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    // 從環境變量獲取 API base URL，如果沒有設置則使用默認值
    const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'http://192.168.2.93:3010/api';

    this.api = axios.create({
      baseURL: apiBaseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 請求攔截器 - 添加 token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 響應攔截器 - 處理 401 錯誤
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // 檢查是否為登錄請求
          const isLoginRequest = error.config?.url?.includes('/auth/login');

          if (isLoginRequest) {
            // 如果是登錄請求失敗，不要清除 token 或重定向
            // 讓錯誤正常傳遞給登錄組件處理
            return Promise.reject(error);
          }

          // 非登錄請求的 401 錯誤：token 過期或無效
          // 檢查當前是否已經在登錄頁面
          const currentPath = window.location.pathname;
          if (currentPath !== '/login') {
            // 清除過期的 token
            localStorage.removeItem('authToken');
            localStorage.removeItem('userInfo');
            // 重定向到登錄頁面（只有在非登錄頁面時）
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // 認證相關 API
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async getCurrentUser(): Promise<AuthUserInfo> {
    const response: AxiosResponse<AuthUserInfo> = await this.api.get('/auth/me');
    return response.data;
  }

  // 通用 GET 請求
  async get<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url);
    return response.data;
  }

  // 通用 POST 請求
  async post<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data);
    return response.data;
  }

  // 通用 PUT 請求
  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data);
    return response.data;
  }

  // 通用 DELETE 請求
  async delete<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url);
    return response.data;
  }

  // DailyReport 相關 API
  async getDailyReports(queryParams?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/dailyreport', { params: queryParams });
    return response.data;
  }

  async getDailyReportById(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/dailyreport/${id}`);
    return response.data;
  }

  async createDailyReport(data: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/dailyreport', data);
    return response.data;
  }

  async updateDailyReport(id: number, data: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/dailyreport/${id}`, data);
    return response.data;
  }

  async deleteDailyReport(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/dailyreport/${id}`);
    return response.data;
  }

  async softDeleteDailyReport(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/dailyreport/${id}/soft-delete`);
    return response.data;
  }

  async getDailyReportTemplate(siteType: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/dailyreport/templates/${siteType}`);
    return response.data;
  }

  // Stage2 相關 API
  async getStage2Data(queryParams?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/Stage2', { params: queryParams });
    return response.data;
  }

  async getStage2TaskById(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/${id}`);
    return response.data;
  }

  async createStage2Task(data: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/Stage2', data);
    return response.data;
  }

  async updateStage2Task(id: number, data: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.put(`/Stage2/${id}`, data);
    return response.data;
  }

  async deleteStage2Task(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.delete(`/Stage2/${id}`);
    return response.data;
  }

  async softDeleteStage2Task(id: number): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post(`/Stage2/${id}/soft-delete`);
    return response.data;
  }

  async getStage2EditData(rfidSNs: string[]): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/Stage2/edit-data', rfidSNs);
    return response.data;
  }

  async multiEditStage2Tasks(data: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.post('/Stage2/multi-edit', data);
    return response.data;
  }

  async getStage2TasksByStage(stage: string, startTime: string, endTime: string, params?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/stage/${stage}`, {
      params: { startTime, endTime, ...params }
    });
    return response.data;
  }

  async getStage2TasksByStaff(staff: string, startTime: string, endTime: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/staff/${staff}`, {
      params: { startTime, endTime }
    });
    return response.data;
  }

  async getStage2TasksByRFID(rfid: string, startTime: string, endTime: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/rfid/${rfid}`, {
      params: { startTime, endTime }
    });
    return response.data;
  }

  async getStage2TasksByCubeMark(cubeMark: string, startTime: string, endTime: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/cube-mark/${cubeMark}`, {
      params: { startTime, endTime }
    });
    return response.data;
  }

  async getStage2TasksBySite(siteName: string, startTime: string, endTime: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/site/${siteName}`, {
      params: { startTime, endTime }
    });
    return response.data;
  }

  async getStage2First3End3(contractId: string, mixId: string, selectedDate: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/Stage2/first3-end3', {
      params: { contractId, mixId, selectedDate }
    });
    return response.data;
  }

  async getStage2Statistics(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/Stage2/statistics');
    return response.data;
  }

  async getStage2StatisticsByDateRange(startTime: string, endTime: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/Stage2/statistics/date-range', {
      params: { startTime, endTime }
    });
    return response.data;
  }

  async validateStage2RFID(rfid: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/validate/rfid/${rfid}`);
    return response.data;
  }

  async validateStage2CubeMark(cubeMark: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/validate/cube-mark/${cubeMark}`);
    return response.data;
  }

  async validateStage2MixId(mixId: string): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get(`/Stage2/validate/mix-id/${mixId}`);
    return response.data;
  }

  async exportStage2ToExcel(queryParams?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/Stage2/export/excel', {
      params: queryParams,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportStage2ToCSV(queryParams?: any): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/Stage2/export/csv', {
      params: queryParams,
      responseType: 'blob'
    });
    return response.data;
  }

  async getStage2Stages(): Promise<any> {
    const response: AxiosResponse<any> = await this.api.get('/Stage2/stages');
    return response.data;
  }

  async getMixID(query: MixIDQueryModel): Promise<MixIDResponse> {
     const params = new URLSearchParams();
    if (query.MixID) params.append('MixID', query.MixID);
    // if (query.Site_Type) params.append('Site_Type', query.Site_Type);
    params.append('Page', query.Page?.toString() || '1');
    params.append('PageSize', query.PageSize?.toString() || '20');
    if (query.SortBy) params.append('SortBy', query.SortBy);
    if (query.SortDirection) params.append('SortDirection', query.SortDirection);

    const response = await this.api.get(`/mixid?${params.toString()}`);
    return response.data;
  }

//   async getTasks(query: TaskQueryModel): Promise<TaskResponse> {
//     const params = new URLSearchParams();
//    if (query.Web_Subroutines_Name) params.append('Web_Subroutines_Name', query.Web_Subroutines_Name);
//    params.append('Page', query.Page?.toString() || '1');
//    params.append('PageSize', query.PageSize?.toString() || '20');

//    const response = await this.api.get(`/mixid/tasks?${params.toString()}`);
//    return response.data;
//  }

  // 下拉選單相關 API
  async getDropdownOptions() {
    const response = await this.api.get('/dropdown/all');
    return response.data;
  }

  async getConcreteSuppliers() {
    const response = await this.api.get('/dropdown/concrete-suppliers');
    return response.data;
  }

  async getMixingPlantLocations() {
    const response = await this.api.get('/dropdown/mixing-plant-locations');
    return response.data;
  }

  async getConPfaSources() {
    const response = await this.api.get('/dropdown/con-pfa-sources');
    return response.data;
  }


  // Contract Data Setting APIs
  async getContracts(query: ContractQueryModel): Promise<ContractResponse> {
    const params = new URLSearchParams();
    if (query.Contract_No) params.append('Contract_No', query.Contract_No);
    if (query.Site_Type) params.append('Site_Type', query.Site_Type);
    params.append('Page', query.Page?.toString() || '1');
    params.append('PageSize', query.PageSize?.toString() || '20');
    if (query.SortBy) params.append('SortBy', query.SortBy);
    if (query.SortDirection) params.append('SortDirection', query.SortDirection);

    const response = await this.api.get(`/Contract?${params.toString()}`);
    return response.data;
  }

  // 獲取所有合約號碼列表（用於下拉選單）
  async getContractNumbers(): Promise<string[]> {
    try {
      // 獲取所有合約，不分頁
      const params = new URLSearchParams();
      params.append('Page', '1');
      params.append('PageSize', '10000'); // 設置一個很大的數字來獲取所有記錄

      const response = await this.api.get(`/Contract?${params.toString()}`);
      const contractResponse: ContractResponse = response.data;

      // 提取所有合約號碼並去重
      const contractNumbers = contractResponse.contractInformation
        .map(contract => contract.contract_No)
        .filter((contractNo): contractNo is string => !!contractNo)
        .filter((value, index, self) => self.indexOf(value) === index) // 去重
        .sort(); // 排序

      return contractNumbers;
    } catch (error) {
      console.error('Error fetching contract numbers:', error);
      return [];
    }
  }

  async getContractByNo(contractNo: string): Promise<ContractModel> {
    const response = await this.api.get(`/Contract/${contractNo}`);
    return response.data;
  }

  async createContract(contract: ContractInformation): Promise<ContractInformation> {
    const response = await this.api.post('/Contract', contract);
    return response.data;
  }

  async updateContract(id: string, contract: ContractInformation): Promise<ContractInformation> {
    const response = await this.api.put(`/Contract/${id}`, contract);
    return response.data;
  }

  async deleteContract(id: number): Promise<void> {
    await this.api.delete(`/Contract/${id}`);
  }

  async createSiteTypeRecord(record: SiteTypeRecord): Promise<SiteTypeRecord> {
    const response = await this.api.post('/Contract/site-type-records', record);
    return response.data;
  }

  async updateSiteTypeRecord(id: number, record: ContractInformation): Promise<SiteTypeRecord> {
    const response = await this.api.put(`/Contract/site-type-records/${id}`, record);
    return response.data;
  }

  async deleteSiteTypeRecord(id: number): Promise<void> {
    await this.api.delete(`/Contract/site-type-records/${id}`);
  }

  async getContractModifyRecords(contractNo: string): Promise<SiteTypeModifyRecord[]> {
    const response = await this.api.get(`/Contract/${contractNo}/modify-records`);
    return response.data;
  }

  async getSlump(query: SiteSlumpQueryModel): Promise<SiteSlumpResponse> {
    const params = new URLSearchParams();
   if (query.Docket_No) params.append('Docket_No', query.Docket_No);
   if (query.RFID) params.append('RFID', query.RFID);
   if (query.Uploaded_By) params.append('Uploaded_By', query.Uploaded_By);
   if (query.Contract_No) params.append('Contract_No', query.Contract_No);
   if (query.startTime) params.append('startTime', query.startTime);
   if (query.endTime) params.append('endTime', query.endTime);
   params.append('Page', query.Page?.toString() || '1');
   params.append('PageSize', query.PageSize?.toString() || '20');
   if (query.SortBy) params.append('SortBy', query.SortBy);
   if (query.SortDirection) params.append('SortDirection', query.SortDirection);

   const response = await this.api.get(`/Site_Slump?${params.toString()}`);
   return response.data;
 }


  async batchCreateSiteSlumps(data: FormData): Promise<any> {
    // Excel 導入可能需要較長時間處理大量數據，設置 5 分鐘超時
    const response = await this.api.post('/Site_Slump/import-excel', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 分鐘 (300000 毫秒) - 適用於大量數據的 Excel 導入
    });
    return response;
  }

  async bulkVerifySiteSlumps(data: any): Promise<any> {
    const response = await this.api.post('/Site_Slump/bulk-verify', data);
    return response;
  }

  // 導出Excel
  async exportSiteSlumpsExcel(request: any): Promise<AxiosResponse<Blob>> {
    return this.api.post('/Site_Slump/export-excel', request, {
      responseType: 'blob',
      timeout: 300000, // 5 分鐘 (300000 毫秒) - 適用於大量數據的 Excel 導出
    });
  }

  // 核對出世紙API
  async getBirthPaperData(query: any): Promise<SiteSlumpResponse> {
    const params = new URLSearchParams();
    if (query.Docket_No) params.append('Docket_No', query.Docket_No);
    if (query.Contract_No) params.append('Contract_No', query.Contract_No);
    if (query.Cube_Mark) params.append('Cube_Mark', query.Cube_Mark);
    if (query.Time_Of_Water_Add) params.append('Time_Of_Water_Add', query.Time_Of_Water_Add);
    if (query.startTime) params.append('startTime', query.startTime);
    if (query.endTime) params.append('endTime', query.endTime);

    const response = await this.api.get(`/Site_Slump/birth-paper?${params.toString()}`);
    return response.data;
  }

  async getBirthPaperStatistics(query: any): Promise<any> {
    const params = new URLSearchParams();
    if (query.Docket_No) params.append('Docket_No', query.Docket_No);
    if (query.Contract_No) params.append('Contract_No', query.Contract_No);
    if (query.Cube_Mark) params.append('Cube_Mark', query.Cube_Mark);
    if (query.Time_Of_Water_Add) params.append('Time_Of_Water_Add', query.Time_Of_Water_Add);
    if (query.startTime) params.append('startTime', query.startTime);
    if (query.endTime) params.append('endTime', query.endTime);

    const response = await this.api.get(`/Site_Slump/birth-paper/statistics?${params.toString()}`);
    return response.data;
  }

  async exportForm17(request: Form17ExportRequest): Promise<Blob> {
    const response = await this.api.post('/Site_Slump/export-form17', request, {
      responseType: 'blob',
      timeout: 300000, // 5 分鐘 (300000 毫秒) - 適用於大量數據的 Excel 導出
    });
    return response.data;
  }

  async getFormLocation(request: Form17ExportRequest): Promise<any> {
    const response = await this.api.post('/Site_Slump/export-form17/locations', request);
    return response.data;
  }

  // Task API methods
  async getTasks(filter:TaskQueryModel): Promise<any> {
    const params = new URLSearchParams();
    if (filter.web_subroutines_name) params.append('WEB_SUBROUTINES_NAME', filter.web_subroutines_name);
    if (filter.page) params.append('page', filter.page.toString());
    if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());

    const response = await this.api.get(`/task?${params.toString()}`);
    return response.data;
  }

  async getTaskById(taskId: string): Promise<any> {
    const response = await this.api.get(`/task/${taskId}`);
    return response.data;
  }

  async approveTaskAction(taskId: number): Promise<any> {
    const response = await this.api.get(`/task/${taskId}/approve`);
    return response.data;
  }

  async rejectTaskAction(taskId: number): Promise<any> {
    const response = await this.api.get(`/task/${taskId}/reject`);
    return response.data;
  }


  async createEditEntry(request: any): Promise<any> {
    const response = await this.api.post('/task/create-edit-entry', request);
    return response.data;
  }

  async executeTaskAction(request: any): Promise<any> {
    const response = await this.api.post('/task/action', request);
    return response.data;
  }



  async getTaskStatistics(): Promise<any> {
    const response = await this.api.get('/Tasks/statistics');
    return response.data;
  }

  // 用戶管理 API 方法

  // 獲取用戶列表
  async getUsers(query?: UserQueryModel): Promise<UserListResponse> {
    const params = new URLSearchParams();
    if (query?.page) params.append('page', query.page.toString());
    if (query?.pageSize) params.append('pageSize', query.pageSize.toString());
    if (query?.searchTerm) params.append('searchTerm', query.searchTerm);
    if (query?.roleFilter) params.append('roleFilter', query.roleFilter);
    if (query?.activeFilter !== undefined) params.append('activeFilter', query.activeFilter.toString());

    const response = await this.api.get(`/user?${params.toString()}`);
    return response.data;
  }

  // 獲取單個用戶
  async getUser(userId: string): Promise<UserInfo> {
    const response: AxiosResponse<UserInfo> = await this.api.get(`/user/${userId}`);
    return response.data;
  }

  // 創建用戶
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<UserInfo>> {
    const response: AxiosResponse<ApiResponse<UserInfo>> = await this.api.post('/user', userData);
    return response.data;
  }

  // 更新用戶
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/user/${userId}`, userData);
    return response.data;
  }

  // 刪除用戶（軟刪除）
  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/user/${userId}`);
    return response.data;
  }

  // 更改密碼
  async changePassword(userId: string, passwordData: ChangePasswordRequest): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/user/${userId}/changePassword`, passwordData);
    return response.data;
  }

  // 更改當前用戶密碼
  async changeCurrentUserPassword(passwordData: ChangePasswordRequest): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.post('/account/changePassword', passwordData);
    return response.data;
  }

  // 註冊新用戶（公開端點）
  async registerUser(userData: CreateUserRequest): Promise<ApiResponse<UserInfo>> {
    const response: AxiosResponse<ApiResponse<UserInfo>> = await this.api.post('/account/register', userData);
    return response.data;
  }

  // 系統配置相關方法
  async getSystemConfig(): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/system/config');
    return response.data;
  }

  async updateSystemConfig(config: any): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put('/system/config', config);
    return response.data;
  }
}

export const apiService = new ApiService();